import React, { useState, useEffect, useRef } from 'react';
import {
  Loader, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronRight,
  FileSearch, Brain, DollarSign, Target, Zap,
  X, RefreshCw, TrendingUp,
  Database, Globe, ArrowRight,
  Sparkles, Activity, Clock, ExternalLink, Package, AlertTriangle,
  Mail, Users
} from 'lucide-react';
import { bidAgentsAPI } from '../../services/api';
import './AgentWorkflowUI.css';

// Phase definitions — 5-phase simple pipeline (no HITL, no agents)
const PHASES = [
  {
    id: 'phase0',
    key: 'phase0',
    phase: 0,
    name: 'Basic Info',
    shortName: 'Info',
    description: 'Fetching tender details, links, and documents from database',
    icon: FileSearch,
    color: '#3b82f6', // Blue
  },
  {
    id: 'phase1',
    key: 'phase1',
    phase: 1,
    name: 'Qualification',
    shortName: 'Qualify',
    description: 'Analyzing bidability, eliminations, and extracting product table',
    icon: Target,
    color: '#6366f1', // Indigo
  },
  {
    id: 'phase2',
    key: 'phase2',
    phase: 2,
    name: 'Arbitrage Opportunity',
    shortName: 'Arbitrage',
    description: 'Historical contract analysis and pricing comparison',
    icon: TrendingUp,
    color: '#10b981', // Emerald
  },
  {
    id: 'phase2b',
    key: 'phase2b',
    phase: '2b',
    name: 'Pricing Research',
    shortName: 'Pricing',
    description: 'Searching web for current market pricing on all products',
    icon: DollarSign,
    color: '#14b8a6', // Teal
  },
  {
    id: 'phase2c',
    key: 'phase2c',
    phase: '2c',
    name: 'Sourcing Strategy',
    shortName: 'Strategy',
    description: 'Comparing overseas vs domestic sourcing with landed-cost analysis',
    icon: Globe,
    color: '#0ea5e9', // Sky blue
  },
  {
    id: 'phase3',
    key: 'phase3',
    phase: 3,
    name: 'Sourcing Package',
    shortName: 'Sourcing',
    description: 'Building supplier directory with contact info and draft emails',
    icon: Globe,
    color: '#f59e0b', // Amber
  },
];

const AgentWorkflowUI = ({ tender, uploadedPdfs, analystConfig, onComplete, onCancel }) => {
  const [jobId, setJobId] = useState(null);
  const [currentPhaseId, setCurrentPhaseId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [phaseResults, setPhaseResults] = useState({});
  const [toolStream, setToolStream] = useState([]);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [currentSubStep, setCurrentSubStep] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [useStreaming, setUseStreaming] = useState(true);

  // Final results state
  const [finalResults, setFinalResults] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  const pollingRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Start analysis on mount
  useEffect(() => {
    setStartTime(Date.now());
    startAnalysis();

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Elapsed time counter
  useEffect(() => {
    if (isProcessing && startTime) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isProcessing, startTime]);

  // Auto-scroll tool stream
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [toolStream]);

  const startAnalysis = async () => {
    try {
      const tenderId = tender.id || tender.tender_id || tender.solicitation_number;

      const request = {
        tender_id: tenderId,
        tender_title: tender.title,
      };

      addToStream('system', 'Starting 5-phase bid analysis pipeline...', 'info');

      if (useStreaming) {
        startStreamingAnalysis(request);
      } else {
        const response = await bidAgentsAPI.startAnalysis(request);
        setJobId(response.job_id);
        addToStream('system', `Job started: ${response.job_id.slice(0, 8)}...`, 'success');
        pollJobStatus(response.job_id);
      }

    } catch (err) {
      console.error('Failed to start analysis:', err);
      setError(err.message || 'Failed to start analysis');
      setIsProcessing(false);
      addToStream('system', `Error: ${err.message}`, 'error');
    }
  };

  const startStreamingAnalysis = (request) => {
    addToStream('system', 'Connecting to real-time stream...', 'info');

    const baseUrl = process.env.REACT_APP_BID_AGENTS_URL || '/api/bid-agents';

    fetch(`${baseUrl}/analyze/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(request),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                console.log('Stream completed');
                break;
              }

              buffer += decoder.decode(value, { stream: true });

              // Process complete events (separated by \n\n)
              const events = buffer.split('\n\n');
              buffer = events.pop() || '';

              for (const eventText of events) {
                if (!eventText.trim() || eventText.startsWith(':')) continue;

                const lines = eventText.split('\n');
                let eventType = null;
                let eventData = null;

                for (const line of lines) {
                  if (line.startsWith('event: ')) {
                    eventType = line.slice(7).trim();
                  } else if (line.startsWith('data: ')) {
                    try {
                      eventData = JSON.parse(line.slice(6));
                    } catch (e) {
                      console.warn('[SSE Parse Error]', line, e);
                    }
                  }
                }

                if (eventType && eventData) {
                  handleStreamEvent(eventType, eventData);
                }
              }
            }
          } catch (err) {
            console.error('Stream reading error:', err);
            addToStream('system', 'Stream interrupted, falling back to polling...', 'warning');
            setUseStreaming(false);
            const pollResponse = await bidAgentsAPI.startAnalysis(request);
            setJobId(pollResponse.job_id);
            pollJobStatus(pollResponse.job_id);
          }
        };

        processStream();
      })
      .catch(err => {
        console.error('Stream connection error:', err);
        addToStream('system', `Stream connection failed: ${err.message}`, 'error');
        addToStream('system', 'Falling back to polling...', 'info');
        setUseStreaming(false);
        bidAgentsAPI.startAnalysis(request).then(response => {
          setJobId(response.job_id);
          addToStream('system', `Job started: ${response.job_id.slice(0, 8)}...`, 'success');
          pollJobStatus(response.job_id);
        });
      });
  };

  const handleStreamEvent = (eventType, data) => {
    console.log('[SSE]', eventType, data);

    switch (eventType) {
      case 'job_start':
        setJobId(data.job_id);
        addToStream('system', `Job started: ${data.job_id?.slice(0, 8)}...`, 'success');
        break;

      case 'phase_start': {
        const phaseKey = data.phase; // "phase0", "phase1", etc.
        setCurrentPhaseId(phaseKey);
        setCurrentSubStep(null);

        // Calculate progress from phase index in PHASES array
        const phaseIndex = PHASES.findIndex(p => p.id === phaseKey);
        setProgress(phaseIndex >= 0 ? Math.round((phaseIndex / PHASES.length) * 100) : progress);

        const phaseName = data.name || getPhaseName(phaseKey);
        addToStream('system', `Starting ${phaseName}...`, 'info');
        break;
      }

      case 'phase_progress':
        setCurrentSubStep(data.message);
        addToStream('system', data.message, 'info');
        break;

      case 'phase_complete': {
        const completedKey = data.phase; // "phase0", "phase1", etc.
        setCurrentSubStep(null);
        setPhaseResults(prev => ({
          ...prev,
          [completedKey]: data.result || { completed: true }
        }));

        if (data.error) {
          addToStream('system', `${data.phase} error: ${data.error}`, 'error');
        } else {
          const name = getPhaseName(completedKey);
          addToStream('system', `${name} complete`, 'success');
        }
        break;
      }

      case 'phase_error':
        addToStream('system', `${data.phase} error: ${data.error}`, 'error');
        break;

      case 'job_complete':
        setIsProcessing(false);
        setProgress(100);
        setCurrentPhaseId('completed');
        setPhaseResults(data.results || {});
        addToStream('system', 'Analysis complete! Review results below.', 'success');

        // Auto-expand all phases
        const expanded = {};
        PHASES.forEach(p => expanded[p.id] = true);
        setExpandedPhases(expanded);

        setFinalResults({ results: data.results, job_id: data.job_id });
        setAnalysisComplete(true);
        break;

      case 'job_error':
        setError(data.error || 'Analysis failed');
        setIsProcessing(false);
        addToStream('system', `Failed: ${data.error}`, 'error');
        break;

      default:
        console.log('Unknown event type:', eventType, data);
    }
  };

  const getPhaseName = (phaseId) => {
    const phase = PHASES.find(p => p.key === phaseId || p.id === phaseId);
    return phase?.name || phaseId;
  };

  const addToStream = (source, message, type = 'info', data = null) => {
    setToolStream(prev => [...prev, {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      source,
      message,
      type,
      data
    }]);
  };

  const pollJobStatus = (jobId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    let consecutiveErrors = 0;
    const MAX_ERRORS = 5;

    pollingRef.current = setInterval(async () => {
      if (consecutiveErrors >= MAX_ERRORS) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        return;
      }
      try {
        const status = await bidAgentsAPI.getJobStatus(jobId);
        consecutiveErrors = 0;

        setProgress(Math.round(status.progress * 100));
        setCurrentPhaseId(status.current_phase);

        if (status.status === 'completed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsProcessing(false);

          const result = await bidAgentsAPI.getJobResult(jobId);
          setPhaseResults(result.results || {});

          addToStream('system', 'Analysis complete! Review results below.', 'success');

          const expanded = {};
          PHASES.forEach(p => expanded[p.id] = true);
          setExpandedPhases(expanded);

          setFinalResults(result);
          setAnalysisComplete(true);

        } else if (status.status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setError(status.error || 'Analysis failed');
          setIsProcessing(false);
          addToStream('system', `Failed: ${status.error}`, 'error');
        }

      } catch (err) {
        consecutiveErrors++;
        console.error(`Polling error (${consecutiveErrors}/${MAX_ERRORS}):`, err);

        if (consecutiveErrors >= MAX_ERRORS) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setError('Lost connection to analysis service');
          setIsProcessing(false);
          addToStream('system', 'Connection lost after multiple retries', 'error');
        }
      }
    }, 1500);
  };

  const transformResults = (result) => {
    const results = result.results || {};
    const p0 = results.phase0 || {};
    const p1 = results.phase1 || {};
    const p2 = results.phase2 || {};
    const p2b = results.phase2b || {};
    const p2c = results.phase2c || {};
    const p3 = results.phase3 || {};

    return {
      config: analystConfig || {},
      phase0: p0,
      phase1: {
        items: (p1.products || []).map((p, idx) => ({
          lineItem: p.line_item || idx + 1,
          productName: p.product_name,
          description: p.description,
          quantity: p.quantity,
          unit: p.unit || 'EA',
        })),
        canBid: p1.can_bid,
        tenderType: p1.tender_type,
        eliminations: p1.eliminations || [],
        totalWholesaleEstimate: p1.total_wholesale_estimate,
        totalRetailEstimate: p1.total_retail_estimate,
      },
      phase2: {
        historicalContracts: (p2.historical_contracts || []).map(c => ({
          title: c.title,
          value: c.value,
          supplier: c.supplier,
          awardDate: c.award_date,
        })),
        pricingComparisons: p2.pricing_comparisons || [],
        bidRecommendation: p2.bid_recommendation,
        arbitrageHeuristic: p2.arbitrage_heuristic,
        recommendedMarkup: p2.recommended_markup_pct,
      },
      phase2b: {
        productPricing: p2b.product_pricing || [],
        pricingSummary: p2b.pricing_summary,
        confidence: p2b.confidence,
      },
      phase2c: {
        sourcingRecommendation: p2c.sourcing_recommendation,
        productStrategies: p2c.product_strategies || [],
        narrative: p2c.narrative,
        confidence: p2c.confidence,
      },
      phase3: {
        productSourcing: p3.product_sourcing || [],
        uniqueSuppliersCount: p3.unique_suppliers_count || 0,
        massOutreachPlan: p3.mass_outreach_plan,
      },
      dataQuality: {
        hasPdfContent: (p1.products || []).length > 0,
        extractedProducts: (p1.products || []).length,
        hasHistoricalData: (p2.historical_contracts || []).length > 0,
        hasSourcingData: (p3.product_sourcing || []).length > 0,
      },
      jobId: result.job_id,
    };
  };

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const getPhaseStatus = (phase) => {
    if (currentPhaseId === phase.id && isProcessing) return 'active';
    if (phaseResults[phase.key]) return 'complete';

    const phaseIndex = PHASES.findIndex(p => p.id === phase.id);
    const currentIndex = PHASES.findIndex(p => p.id === currentPhaseId);

    if (phaseIndex < currentIndex) return 'complete';
    return 'pending';
  };

  const renderPhaseData = (phase) => {
    const data = phaseResults[phase.key];
    if (!data) return null;

    switch (phase.key) {
      case 'phase0': {
        return (
          <div className="phase-data">
            <div className="data-stat">
              <span className="stat-value">{data.title || 'N/A'}</span>
              <span className="stat-label">Tender Title</span>
            </div>
            <div className="data-row">
              <div className="data-stat">
                <span className="stat-value">{data.organization || 'N/A'}</span>
                <span className="stat-label">Organization</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{data.closing_date || 'N/A'}</span>
                <span className="stat-label">Closing Date</span>
              </div>
            </div>
            {data.document_links?.length > 0 && (
              <div className="data-item">
                <FileSearch size={14} />
                <span>{data.document_links.length} documents found</span>
              </div>
            )}
            {data.platform_urls?.length > 0 && (
              <div className="data-item">
                <ExternalLink size={14} />
                <span>{data.platform_urls.length} platform link(s)</span>
              </div>
            )}
          </div>
        );
      }

      case 'phase1': {
        const products = data.products || [];
        const eliminations = data.eliminations || [];
        return (
          <div className="phase-data">
            <div className="data-row">
              <div className="data-stat highlight">
                <span className="stat-value">
                  {data.can_bid ? 'Yes' : 'No'}
                </span>
                <span className="stat-label">Can Bid?</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{data.tender_type || 'Unknown'}</span>
                <span className="stat-label">Tender Type</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{data.confidence || 'N/A'}</span>
                <span className="stat-label">Confidence</span>
              </div>
            </div>

            {eliminations.length > 0 && (
              <div className="eliminations-list">
                {eliminations.map((e, i) => (
                  <div key={i} className={`data-item ${e.severity === 'hard_stop' ? 'error' : 'warning'}`}>
                    <AlertTriangle size={14} />
                    <span>{e.description}</span>
                    <span className="data-badge">{e.severity === 'hard_stop' ? 'Blocker' : 'Review'}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="data-stat">
              <span className="stat-value">{products.length}</span>
              <span className="stat-label">Products Extracted</span>
            </div>

            {products.slice(0, 3).map((item, i) => (
              <div key={i} className="data-item">
                <Package size={14} />
                <span>{item.product_name}</span>
                {item.quantity && <span className="data-badge">Qty: {item.quantity}</span>}
              </div>
            ))}
            {products.length > 3 && (
              <div className="data-item muted">
                <span>...and {products.length - 3} more</span>
              </div>
            )}

            {(data.total_wholesale_estimate > 0 || data.total_retail_estimate > 0) && (
              <div className="data-row" style={{ marginTop: '8px' }}>
                <div className="data-stat">
                  <span className="stat-value">
                    ${data.total_wholesale_estimate?.toLocaleString() || '—'}
                  </span>
                  <span className="stat-label">Wholesale Est.</span>
                </div>
                <div className="data-stat">
                  <span className="stat-value">
                    ${data.total_retail_estimate?.toLocaleString() || '—'}
                  </span>
                  <span className="stat-label">Retail Est.</span>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'phase2': {
        const contracts = data.historical_contracts || [];
        const comparisons = data.pricing_comparisons || [];
        return (
          <div className="phase-data">
            <div className="data-row">
              <div className="data-stat highlight">
                <span className="stat-value">{data.bid_recommendation || 'N/A'}</span>
                <span className="stat-label">Recommendation</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{contracts.length}</span>
                <span className="stat-label">Historical Contracts</span>
              </div>
              {data.recommended_markup_pct && (
                <div className="data-stat">
                  <span className="stat-value">{data.recommended_markup_pct}%</span>
                  <span className="stat-label">Recommended Markup</span>
                </div>
              )}
            </div>

            {data.arbitrage_heuristic && (
              <div className="data-item">
                <Sparkles size={14} />
                <span className="truncate">{data.arbitrage_heuristic.slice(0, 120)}...</span>
              </div>
            )}

            {contracts.slice(0, 2).map((c, i) => (
              <div key={i} className="data-item contract">
                <Database size={14} />
                <span className="truncate">{c.title}</span>
                {c.value && <span className="data-badge">${(c.value / 1000).toFixed(0)}K</span>}
              </div>
            ))}

            {comparisons.length > 0 && (
              <div className="data-stat" style={{ marginTop: '8px' }}>
                <span className="stat-value">{comparisons.length}</span>
                <span className="stat-label">Product Pricing Comparisons</span>
              </div>
            )}
          </div>
        );
      }

      case 'phase2b': {
        const pricing = data.product_pricing || [];
        const priced = pricing.filter(p => p.listed_price);
        return (
          <div className="phase-data">
            <div className="data-row">
              <div className="data-stat highlight">
                <span className="stat-value">{priced.length}/{pricing.length}</span>
                <span className="stat-label">Products Priced</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{data.confidence || 'N/A'}</span>
                <span className="stat-label">Confidence</span>
              </div>
            </div>

            {priced.slice(0, 3).map((p, i) => (
              <div key={i} className="data-item">
                <DollarSign size={14} />
                <span>{p.product_name}</span>
                <span className="data-badge">
                  ${typeof p.listed_price === 'number' ? p.listed_price.toLocaleString() : p.listed_price}
                  {p.retailer && ` — ${p.retailer}`}
                </span>
              </div>
            ))}
            {priced.length > 3 && (
              <div className="data-item muted">
                <span>...and {priced.length - 3} more priced</span>
              </div>
            )}

            {data.pricing_summary && (
              <div className="data-item">
                <Sparkles size={14} />
                <span className="truncate">{data.pricing_summary.slice(0, 120)}...</span>
              </div>
            )}
          </div>
        );
      }

      case 'phase2c': {
        const strategies = data.product_strategies || [];
        const rec = data.sourcing_recommendation || 'N/A';
        const recColor = rec === 'OVERSEAS' ? '#ef4444' : rec === 'HYBRID' ? '#f59e0b' : '#10b981';
        return (
          <div className="phase-data">
            <div className="data-row">
              <div className="data-stat highlight" style={{ borderColor: recColor }}>
                <span className="stat-value" style={{ color: recColor }}>{rec}</span>
                <span className="stat-label">Recommendation</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{strategies.length}</span>
                <span className="stat-label">Products Analyzed</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{data.confidence || 'N/A'}</span>
                <span className="stat-label">Confidence</span>
              </div>
            </div>

            {strategies.slice(0, 3).map((ps, i) => (
              <div key={i} className="data-item">
                <Package size={14} />
                <span>{ps.product_name}</span>
                <span className="data-badge" style={{
                  background: ps.recommendation === 'OVERSEAS' ? '#fef2f2' : '#f0fdf4',
                  color: ps.recommendation === 'OVERSEAS' ? '#dc2626' : '#16a34a'
                }}>
                  {ps.recommendation}
                  {ps.savings_pct != null && ` (${ps.savings_pct > 0 ? '+' : ''}${ps.savings_pct.toFixed(0)}%)`}
                </span>
              </div>
            ))}
            {strategies.length > 3 && (
              <div className="data-item muted">
                <span>...and {strategies.length - 3} more products</span>
              </div>
            )}

            {data.narrative && (
              <div className="data-item">
                <Sparkles size={14} />
                <span className="truncate">{data.narrative.slice(0, 140)}...</span>
              </div>
            )}
          </div>
        );
      }

      case 'phase3': {
        const sourcing = data.product_sourcing || [];
        return (
          <div className="phase-data">
            <div className="data-row">
              <div className="data-stat highlight">
                <span className="stat-value">{data.unique_suppliers_count || 0}</span>
                <span className="stat-label">Suppliers Found</span>
              </div>
              <div className="data-stat">
                <span className="stat-value">{sourcing.length}</span>
                <span className="stat-label">Products Sourced</span>
              </div>
            </div>

            {sourcing.slice(0, 2).map((ps, i) => (
              <div key={i} className="data-item">
                <Users size={14} />
                <span>{ps.product_name}</span>
                <span className="data-badge">{ps.suppliers?.length || 0} suppliers</span>
              </div>
            ))}

            {data.mass_outreach_plan && (
              <div className="data-item">
                <Mail size={14} />
                <span className="truncate">{data.mass_outreach_plan.slice(0, 100)}...</span>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="agent-workflow-ui">
      {/* Header */}
      <div className="workflow-header">
        <div className="header-content">
          <div className="header-icon">
            <Brain size={28} />
            <div className="pulse-ring" />
          </div>
          <div className="header-text">
            <h2>AI Bid Analysis</h2>
            <p>5-phase Claude analysis pipeline</p>
          </div>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <Clock size={16} />
            <span>{formatTime(elapsedTime)}</span>
          </div>
          <div className="stat-item">
            <Activity size={16} />
            <span>{Object.keys(phaseResults).length}/{PHASES.length} phases</span>
          </div>
          <div className="stat-item progress-stat">
            <div className="mini-progress">
              <div className="mini-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span>{progress}%</span>
          </div>
        </div>
      </div>

      {/* Pipeline Visualization */}
      <div className="pipeline-flow">
        {PHASES.map((phase, index) => {
          const status = getPhaseStatus(phase);
          const Icon = phase.icon;

          return (
            <React.Fragment key={phase.id}>
              <div
                className={`pipeline-node ${status}`}
                style={{ '--phase-color': phase.color }}
                onClick={() => togglePhase(phase.id)}
              >
                <div className="node-icon">
                  {status === 'complete' ? (
                    <CheckCircle size={20} />
                  ) : status === 'active' ? (
                    <Loader size={20} className="spinning" />
                  ) : (
                    <Icon size={20} />
                  )}
                </div>
                <span className="node-label">{phase.shortName}</span>
                {status === 'active' && currentSubStep && (
                  <span className="node-substep">{currentSubStep}</span>
                )}
              </div>
              {index < PHASES.length - 1 && (
                <div className={`pipeline-connector ${status === 'complete' ? 'complete' : ''}`}>
                  <ArrowRight size={16} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="workflow-content">
        {/* Phases Panel */}
        <div className="phases-panel">
          {PHASES.map((phase) => {
            const status = getPhaseStatus(phase);
            const isExpanded = expandedPhases[phase.id];
            const Icon = phase.icon;

            return (
              <div
                key={phase.id}
                className={`phase-card ${status}`}
                style={{ '--phase-color': phase.color }}
              >
                <div className="phase-header" onClick={() => togglePhase(phase.id)}>
                  <div className="phase-info">
                    <div className="phase-icon">
                      {status === 'complete' ? (
                        <CheckCircle size={18} />
                      ) : status === 'active' ? (
                        <Loader size={18} className="spinning" />
                      ) : (
                        <Icon size={18} />
                      )}
                    </div>
                    <div className="phase-text">
                      <span className="phase-number">Phase {phase.phase}</span>
                      <span className="phase-name">{phase.name}</span>
                    </div>
                  </div>
                  <div className="phase-toggle">
                    {status === 'active' && (
                      <span className="active-badge">{currentSubStep || 'Running'}</span>
                    )}
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="phase-content">
                    <p className="phase-description">{phase.description}</p>
                    {renderPhaseData(phase)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tool Stream Panel */}
        <div className="stream-panel">
          <div className="stream-header">
            <Zap size={16} />
            <span>Live Stream</span>
          </div>
          <div className="stream-content" ref={streamRef}>
            {toolStream.map((item) => {
              const isSystem = item.source === 'system';

              return (
                <div key={item.id} className={`stream-item ${item.type} ${isSystem ? 'system' : ''}`}>
                  <div className="stream-icon">
                    {isSystem ? (
                      <Brain size={14} />
                    ) : item.type === 'success' ? (
                      <CheckCircle size={14} />
                    ) : item.type === 'error' ? (
                      <XCircle size={14} />
                    ) : (
                      <Zap size={14} />
                    )}
                  </div>
                  <div className="stream-content-inner">
                    {!isSystem && <span className="stream-tool">{item.source}</span>}
                    <span className="stream-message">{item.message}</span>
                  </div>
                  <span className="stream-time">
                    {item.timestamp.toLocaleTimeString('en-US', {
                      hour12: false,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </span>
                </div>
              );
            })}
            {isProcessing && (
              <div className="stream-cursor">
                <span className="cursor-dot" />
                <span className="cursor-dot" />
                <span className="cursor-dot" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="workflow-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => { setError(null); startAnalysis(); }}>
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="workflow-footer">
        <div className="tender-info">
          <span className="tender-title">{tender?.title || 'Selected Tender'}</span>
          <span className="tender-meta">
            {tender?.organization?.contracting_entity || tender?.organization?.name}
          </span>
        </div>
        {analysisComplete && finalResults && (
          <button
            className="generate-report-btn"
            onClick={() => onComplete(transformResults(finalResults))}
          >
            <FileSearch size={16} />
            Generate Final Report
          </button>
        )}
        <button className="cancel-btn" onClick={onCancel}>
          <X size={16} />
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AgentWorkflowUI;
